import { getShowSmartfitWidget } from "@/utils/product-detail-page/pdp.utils";

export const D2C_PRODUCT_DETAIL_SEGMENT = "product";

export interface D2CPdpUrlParams {
  country: string;
  language: string;
  productSlug: string;
  articleCode?: string;
  addFitFinderParams?: boolean;
}

export interface D2CPdpProductLike {
  code?: string;
  name?: string;
  url?: string;
  urlName?: string;
  variantProducts?: Array<{ merchandiseCategory?: string | null }>;
}

export function normalizeD2CPdpUrlPart(
  value: string | null | undefined
): string | null {
  const trimmedValue = value?.trim();

  if (!trimmedValue) {
    return null;
  }

  return encodeURIComponent(trimmedValue);
}

export function normalizeD2CPdpSlug(
  value: string | null | undefined
): string | null {
  const trimmedValue = value?.trim();

  if (!trimmedValue) {
    return null;
  }

  let decodedValue = trimmedValue;
  try {
    decodedValue = decodeURIComponent(trimmedValue);
  } catch (_error) {
    decodedValue = trimmedValue;
  }

  const slug = decodedValue
    .normalize("NFKD")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");

  return slug || null;
}

function getSlugCandidateFromUrl(
  url: string | undefined,
  productCode: string | undefined
): string | undefined {
  if (!url) {
    return;
  }

  const path = url.split("?")[0] ?? "";
  const segments = path.split("/").filter(Boolean);

  return segments
    .reverse()
    .find((segment) => segment !== productCode && segment !== "product");
}

export function getD2CPdpProductSlug(
  product: D2CPdpProductLike
): string | null {
  return normalizeD2CPdpSlug(
    product.urlName ??
      getSlugCandidateFromUrl(product.url, product.code) ??
      product.name ??
      product.code
  );
}

export function buildD2CPdpUrl(params: D2CPdpUrlParams): string | null {
  const country = normalizeD2CPdpUrlPart(params.country);
  const language = normalizeD2CPdpUrlPart(params.language);
  const productSlug = normalizeD2CPdpSlug(params.productSlug);

  if (!(country && language && productSlug)) {
    return null;
  }

  const baseUrl = `/${country}/${language}/${D2C_PRODUCT_DETAIL_SEGMENT}/${productSlug}`;
  const searchParams = new URLSearchParams();
  const articleCode = params.articleCode?.trim();

  if (articleCode) {
    searchParams.set("article", articleCode);
  }

  if (params.addFitFinderParams) {
    searchParams.set("fita.fitFinder.subdomain", "widget");
    searchParams.set("fita.fitFinder.protocol", "https");
  }

  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

export function buildD2CPdpUrlForProduct(params: {
  country: string;
  language: string;
  product: D2CPdpProductLike;
  articleCode?: string;
}): string | null {
  const productSlug = getD2CPdpProductSlug(params.product);

  if (!productSlug) {
    return null;
  }

  const showSmartfit = getShowSmartfitWidget(
    params.product.variantProducts ?? []
  );

  return buildD2CPdpUrl({
    country: params.country,
    language: params.language,
    productSlug,
    articleCode: params.articleCode,
    addFitFinderParams: !showSmartfit && process.env.NODE_ENV === "development",
  });
}
